import React, { useState, useRef, useEffect } from 'react';
import { Send, Zap, Mic, ArrowLeft, AlertTriangle, TrendingUp, ClipboardList, Paperclip, FileText, Clock, Check, Play, Eye, Download } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { FINANCE_REPORTS_DATA, FINANCE_REPORTS_CHAT_RESPONSES } from '../../data/financeReportsData';

const MetricCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  return (
    <div className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} border ${border} rounded-xl px-3 py-2.5 text-center`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const InsightCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[9px] mt-1 ${ts}`}>Finn · {item.time}</p>
      </div>
    </div>
  );
};

const STATUS_CONFIG = {
  'draft-ready': { label: 'Draft Ready', icon: FileText, color: 'indigo' },
  'in-review': { label: 'In Review', icon: Eye, color: 'amber' },
  'in-progress': { label: 'In Progress', icon: Clock, color: 'cyan' },
  'scheduled': { label: 'Scheduled', icon: Clock, color: 'slate' },
  'sent': { label: 'Sent', icon: Check, color: 'emerald' },
  'completed': { label: 'Completed', icon: Check, color: 'emerald' },
};

const V4FinanceReports = ({ isDark, onWorkflowToggle, workflowPanelOpen }) => {
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || FINANCE_REPORTS_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };
  const handleCta = (label, detail) => {
    sendMessage(`${label} — ${detail}`, { text: `I'll handle "${label}" for ${detail}. Setting that up now and will confirm once ready.\n\nAnything else you need?` });
  };

  const d = FINANCE_REPORTS_DATA;

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Finn about reports..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="reports-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.finn} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Finn</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg ${hoverBg}`}><Paperclip className={`w-3.5 h-3.5 ${ts}`} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="reports-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="reports-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="reports-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Reports</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.finn} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  const colorMap = {
    indigo: { bg: isDark ? 'bg-indigo-500/10' : 'bg-indigo-50', text: isDark ? 'text-indigo-400' : 'text-indigo-600' },
    amber: { bg: isDark ? 'bg-amber-500/10' : 'bg-amber-50', text: isDark ? 'text-amber-400' : 'text-amber-600' },
    cyan: { bg: isDark ? 'bg-cyan-500/10' : 'bg-cyan-50', text: isDark ? 'text-cyan-400' : 'text-cyan-600' },
    slate: { bg: isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]', text: isDark ? 'text-white/50' : 'text-slate-500' },
    emerald: { bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50', text: isDark ? 'text-emerald-400' : 'text-emerald-600' },
  };

  return (
    <div className="flex flex-col h-full" data-testid="reports-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardList className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Reports</p>
          </div>
          <button onClick={() => handleCta('Generate Report', 'Create a new financial report')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="reports-generate-btn">
            <Play className="w-3.5 h-3.5" /> Generate Report
          </button>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
        <div className="grid grid-cols-6 gap-3">
          {d.metrics.map((m, i) => <MetricCard key={i} item={m} isDark={isDark} />)}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* P&L Summary */}
          <div className={`${cardBg} border ${border} rounded-xl p-4 flex flex-col`} data-testid="pnl-section">
            <p className={`text-[11px] font-semibold mb-3 ${tp}`}>P&L Summary — Q1 FY25</p>
            <div className="flex-1">
              <div className={`grid grid-cols-[2.5fr_1.2fr_1.2fr_1fr] items-center px-2 py-1.5 mb-1 border-b ${border}`}>
                <span className={`text-[8px] font-semibold ${ts}`}>Line Item</span>
                <span className={`text-[8px] font-semibold text-right ${ts}`}>Actual</span>
                <span className={`text-[8px] font-semibold text-right ${ts}`}>Budget</span>
                <span className={`text-[8px] font-semibold text-right ${ts}`}>Var</span>
              </div>
              {d.pnl.map((row, i) => {
                const isPositiveVar = row.variance.startsWith('+');
                const isBold = row.bold;
                return (
                  <div key={i} className={`grid grid-cols-[2.5fr_1.2fr_1.2fr_1fr] items-center px-2 py-1.5 ${isBold ? `border-t ${border} mt-0.5` : ''}`}>
                    <span className={`text-[10px] ${isBold ? `font-semibold ${tp}` : ts}`}>{row.line}</span>
                    <span className={`text-[10px] text-right ${isBold ? `font-bold ${tp}` : tp}`}>
                      {row.q1Actual < 0 ? '-' : ''}${(Math.abs(row.q1Actual) / 1000).toFixed(0)}K
                    </span>
                    <span className={`text-[10px] text-right ${ts}`}>
                      {row.q1Budget < 0 ? '-' : ''}${(Math.abs(row.q1Budget) / 1000).toFixed(0)}K
                    </span>
                    <span className={`text-[9px] text-right font-medium ${isPositiveVar ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{row.variance}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Performance Benchmarks */}
          <div className={`${cardBg} border ${border} rounded-xl p-4 flex flex-col`} data-testid="ratios-section">
            <p className={`text-[11px] font-semibold mb-3 ${tp}`}>Performance Benchmarks</p>
            <div className="flex-1 grid grid-cols-2 gap-3 content-start">
              {d.ratios.map((r, i) => (
                <div key={i} className={`px-3 py-3 rounded-lg border ${border}`}>
                  <p className={`text-[9px] ${ts}`}>{r.label}</p>
                  <p className={`text-lg font-bold mt-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{r.value}</p>
                  <p className={`text-[9px] mt-1 ${ts}`}>Benchmark: {r.benchmark}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Report Queue */}
        <div className={`${cardBg} border ${border} rounded-xl overflow-hidden`} data-testid="report-queue-section">
          <div className="flex items-center gap-2 px-4 py-2.5">
            <FileText className={`w-3.5 h-3.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-[11px] font-semibold ${tp}`}>Report Queue</p>
            <span className={`text-[9px] ${ts}`}>{d.reportQueue.filter(r => !['sent', 'completed'].includes(r.status)).length} active</span>
          </div>
          <div className={`grid grid-cols-[2.5fr_1fr_1fr_1fr_1fr_1.5fr] items-center px-4 py-1.5 border-t border-b ${border}`}>
            <span className={`text-[8px] font-semibold ${ts}`}>Report</span>
            <span className={`text-[8px] font-semibold text-center ${ts}`}>Type</span>
            <span className={`text-[8px] font-semibold text-center ${ts}`}>Status</span>
            <span className={`text-[8px] font-semibold text-center ${ts}`}>Due</span>
            <span className={`text-[8px] font-semibold text-center ${ts}`}>Updated</span>
            <span className={`text-[8px] font-semibold text-right ${ts}`}>Action</span>
          </div>
          {d.reportQueue.map(r => {
            const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.scheduled;
            const cm = colorMap[sc.color] || colorMap.slate;
            const isDone = ['sent', 'completed'].includes(r.status);
            const isReady = r.status === 'draft-ready';
            const isReview = r.status === 'in-review';
            let ctaLabel = 'View';
            let ctaAction = 'View Report';
            if (isReady) { ctaLabel = 'Review Draft'; ctaAction = 'Review Draft'; }
            else if (isReview) { ctaLabel = 'Approve'; ctaAction = 'Approve Report'; }
            else if (isDone) { ctaLabel = 'Download'; ctaAction = 'Download Report'; }

            return (
              <div key={r.id} className={`grid grid-cols-[2.5fr_1fr_1fr_1fr_1fr_1.5fr] items-center px-4 py-2 border-b ${border} last:border-0`} data-testid={`report-${r.id}`}>
                <div>
                  <p className={`text-[10px] font-medium ${tp}`}>{r.name}</p>
                  <p className={`text-[8px] ${ts}`}>{r.assignee}{r.pages ? ` · ${r.pages} pages` : ''}</p>
                </div>
                <span className={`text-[9px] text-center ${ts}`}>{r.type}</span>
                <div className="flex justify-center">
                  <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full ${cm.bg} ${cm.text}`}>{sc.label}</span>
                </div>
                <span className={`text-[9px] text-center ${ts}`}>{r.dueDate}</span>
                <span className={`text-[8px] text-center ${ts}`}>{r.lastUpdated}</span>
                <div className="flex justify-end">
                  <button onClick={() => handleCta(ctaAction, r.name)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${
                    isReady ? `${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}` :
                    isReview ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' :
                    isDone ? `${isDark ? 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08]' : 'bg-black/[0.03] text-slate-500 hover:bg-black/[0.06]'}` :
                    `${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`
                  }`} data-testid={`report-cta-${r.id}`}>
                    {isDone ? <Download className="w-3 h-3" /> : isReview ? <Eye className="w-3 h-3" /> : <FileText className="w-3 h-3" />} {ctaLabel}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Insights */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src={AGENT_THUMBS.finn} alt="" className="w-4 h-4 rounded-full object-cover" />
            <p className={`text-xs font-semibold ${tp}`}>Finn's Insights</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {d.insights.map(item => <InsightCard key={item.id} item={item} isDark={isDark} />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4FinanceReports;
